/**
 * Inicio del sitio (finance360.cr). Portada con foto real y efecto de inmersión: al
 * mover el mouse la foto y la ventana se desplazan; al entrar, un video lleva del
 * escritorio a Finance 360 corriendo. Los videos son grabaciones reales del sistema con
 * una empresa ficticia (Café Altura S.A.), sin datos de clientes.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Marco, { Navegacion, useMeta } from './Marco';
import { useT } from './idioma';
import { usePlanes } from './planes';

export const VIDEO = (n) => `/sitio/${n}.mp4`;
const reduceMovimiento = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

export function Ventana({ clip, etiqueta, url, auto = true, onEnded, loop = true, className = '' }) {
  return (
    <div className={`ventana ${className}`}>
      <div className="barra"><i /><i /><i />{url && <span className="mono">{url}</span>}</div>
      <video key={clip} src={VIDEO(clip)} autoPlay={auto} muted loop={loop} playsInline onEnded={onEnded} aria-label={etiqueta} />
    </div>
  );
}

/** Temporizador propio de cada bloque animado: solo ese bloque se vuelve a dibujar. */
function useReloj(activo, ms = 150) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!activo) return undefined;
    const id = setInterval(() => setN(n => n + 1), ms);
    return () => clearInterval(id);
  }, [activo, ms]);
}

function Portada() {
  const T = useT();
  const t = T.inicio;
  const [mov, setMov] = useState({ x: 0, y: 0 });
  const [fase, setFase] = useState('afuera');   // afuera → video → adentro
  const raf = useRef(null);
  const respaldo = useRef(null);
  const adentro = fase === 'adentro';

  const mover = (e) => {
    if (fase !== 'afuera' || reduceMovimiento()) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => setMov({ x, y }));
  };
  const entrar = () => {
    if (fase !== 'afuera') return;
    if (reduceMovimiento()) { setFase('adentro'); return; }
    setFase('video');
    clearTimeout(respaldo.current);
    respaldo.current = setTimeout(() => setFase('adentro'), 7400);
  };
  const terminar = () => { clearTimeout(respaldo.current); setFase('adentro'); };
  useEffect(() => () => { clearTimeout(respaldo.current); cancelAnimationFrame(raf.current); }, []);

  const { x, y } = fase === 'afuera' ? mov : { x: 0, y: 0 };
  return (
    <header className="portada" onMouseMove={mover} onMouseLeave={() => setMov({ x: 0, y: 0 })}>
      <img className="foto" src="/sitio/escritorio.jpg" alt="" aria-hidden="true"
        style={{ transform: `translate(${x * -18}px, ${y * -12}px) scale(${adentro ? 1.5 : 1.04})`, filter: adentro ? 'blur(8px) brightness(.35)' : 'none' }} />
      <div className="velo" aria-hidden="true" />
      <div className="tinta" aria-hidden="true" style={{ opacity: adentro ? 0.8 : 0 }} />
      {fase === 'video' && <video className="inmersion" src={VIDEO('inmersion')} autoPlay muted playsInline onEnded={terminar} aria-label={t.videoAlt} />}

      <Navegacion tono="oscura" />

      <div className="copia caja" style={{ opacity: fase === 'afuera' ? 1 : 0, transform: `translateY(${adentro ? -60 : 0}px)`, pointerEvents: fase === 'afuera' ? 'auto' : 'none' }}>
        <h1 className="display h1" style={{ color: '#fff', maxWidth: 1000, margin: '0 auto' }}>{t.titulo}</h1>
        <p className="bajada">{t.bajada}</p>
        <form onSubmit={(e) => { e.preventDefault(); const c = e.currentTarget.elements.correo.value; window.location.href = `/demo${c ? `?correo=${encodeURIComponent(c)}` : ''}`; }}>
          <label htmlFor="portada-correo" className="solo-lector">{t.correo}</label>
          <input id="portada-correo" name="correo" type="email" placeholder={t.correo} />
          <button type="submit" className="pildora primaria">{T.nav.demo}</button>
        </form>
        <button type="button" className="entrar" onClick={entrar}>{t.entrar} ↓</button>
      </div>

      <div className="dentro-cap" style={{ opacity: adentro ? 1 : 0, pointerEvents: adentro ? 'auto' : 'none', transitionDelay: adentro ? '.4s' : '0s' }}>
        <span className="mono" style={{ fontSize: 13, color: '#C9CEEA' }}>{t.dentro}</span>
        <span style={{ display: 'flex', gap: 10 }}>
          <a href="#product" className="pildora blanca" style={{ minHeight: 40, fontSize: 14 }}>{t.seguir} ↓</a>
          <button type="button" className="pildora borde" style={{ minHeight: 40, fontSize: 14 }} onClick={() => setFase('afuera')}>← {t.volver}</button>
        </span>
      </div>

      <div className={`ventana-portada${adentro ? ' adentro' : ''}`} onClick={entrar} role="presentation"
        style={{ transform: `translateX(-50%) translateY(${adentro ? 0 : 520}px) scale(${adentro ? 1 : 0.6}) rotateX(${y * -4}deg) rotateY(${x * 5}deg)` }}>
        <Ventana clip="dashboard" etiqueta={t.ventanaAlt} url="app.finance360.cr" />
      </div>
    </header>
  );
}

function Recorrido() {
  const t = useT().inicio;
  const [i, setI] = useState(0);
  const [pausa, setPausa] = useState(reduceMovimiento());
  const inicio = useRef(Date.now());
  useReloj(!pausa, 150);
  const DUR = 7000;
  const avance = pausa ? 0 : Math.min(1, (Date.now() - inicio.current) / DUR);
  useEffect(() => {
    if (!pausa && avance >= 1) { inicio.current = Date.now(); setI(n => (n + 1) % t.tour.length); }
  });
  const elegir = (n) => { inicio.current = Date.now(); setI(n); };
  const actual = t.tour[i];
  return (
    <section id="product" className="noche">
      <div className="caja dos" style={{ paddingTop: 140, paddingBottom: 140, gap: 72 }}>
        <div className="texto recorrido" style={{ maxWidth: 440 }}>
          <h2 className="display" style={{ fontSize: 'clamp(32px,3.6vw,48px)', lineHeight: 1.08 }}>{t.todo}</h2>
          <div style={{ marginTop: 48 }}>
            {t.tour.map(([titulo, texto], n) => (
              <div key={titulo} className={`item${n === i ? ' activo' : ''}`}>
                <div className="progreso" aria-hidden="true" style={{ width: `${n === i ? avance * 100 : 0}%` }} />
                <button type="button" aria-expanded={n === i} onClick={() => elegir(n)}>{titulo}</button>
                {n === i && <p className="entra suave" style={{ margin: '10px 0 0', fontSize: 15.5 }}>{texto}</p>}
              </div>
            ))}
            <div style={{ borderTop: '1px solid rgba(255,255,255,.12)', paddingTop: 22, display: 'flex', alignItems: 'center', gap: 16 }}>
              <Link to="/tour" style={{ color: '#fff', fontWeight: 500 }}>{t.lanzar} ›</Link>
              <button type="button" className="redondo" style={{ marginLeft: 'auto' }} aria-label={pausa ? t.reanudar : t.pausar}
                onClick={() => { inicio.current = Date.now(); setPausa(!pausa); }}>{pausa ? '▶' : '❚❚'}</button>
            </div>
          </div>
        </div>
        <div className="medio" style={{ minHeight: 480, display: 'flex', alignItems: 'center' }}>
          <Ventana key={actual[2]} className="entra" clip={actual[2]} etiqueta={actual[0]} />
        </div>
      </div>
    </section>
  );
}

function VerloCorrer() {
  const t = useT().inicio;
  const [i, setI] = useState(0);
  const clip = t.clips[i];
  return (
    <section aria-label={t.verloTitulo} style={{ background: '#161944', color: '#fff' }}>
      <div className="caja" style={{ paddingTop: 110, paddingBottom: 130 }}>
        <h2 className="display" style={{ fontSize: 'clamp(30px,3.4vw,46px)', textAlign: 'center' }}>{t.verloTitulo}</h2>
        <div style={{ maxWidth: 1000, margin: '56px auto 0' }}>
          <Ventana clip={clip[1]} etiqueta={clip[0]} url="cafe-altura.finance360.cr" loop={false} onEnded={() => setI((i + 1) % t.clips.length)} />
        </div>
        <div role="tablist" aria-label={t.verloTitulo} style={{ display: 'flex', gap: '8px 28px', justifyContent: 'center', flexWrap: 'wrap', marginTop: 26 }}>
          {t.clips.map(([nombre], n) => (
            <button key={nombre} type="button" role="tab" aria-selected={n === i} onClick={() => setI(n)}
              style={{ background: 'none', border: 0, borderBottom: `2px solid ${n === i ? '#8E98F0' : 'transparent'}`, color: n === i ? '#fff' : '#AEB4DD', font: 'inherit', fontSize: 15, fontWeight: n === i ? 600 : 400, padding: '10px 2px', minHeight: 44, cursor: 'pointer' }}>{nombre}</button>
          ))}
        </div>
        <p style={{ textAlign: 'center', color: '#AEB4DD', fontSize: 14, margin: '18px 0 0' }}>{t.verloNota}</p>
      </div>
    </section>
  );
}

function Mosaico({ n, desfase }) {
  const t = useT().inicio;
  const u = t.tileUi;
  const [pausa, setPausa] = useState(reduceMovimiento());
  const base = useRef(Date.now() - desfase * 1000);
  const congelado = useRef(desfase);
  useReloj(!pausa, 150);
  const seg = pausa ? congelado.current : ((Date.now() - base.current) / 1000) % 6;
  const p = seg / 6;
  const alternar = () => {
    if (pausa) { base.current = Date.now() - congelado.current * 1000; } else { congelado.current = seg; }
    setPausa(!pausa);
  };
  const fila = (a, b, si) => <div key={a} className={`fila ${si ? 'si' : 'no'}`}><span>{a}</span><span className="mono">{b}</span></div>;
  let cab, chip, filas, pie, barra = null;
  if (n === 0) {
    const e = p < 0.22 ? 0 : p < 0.45 ? 1 : 2;
    cab = u.fe; chip = e === 2 ? ['ok', u.aceptada] : ['info', u.enviando];
    filas = [fila(u.firmada, '✓', true), fila(u.enviada, e >= 1 ? '✓' : '…', e >= 1), fila(u.ok, e >= 2 ? '✓' : '…', e >= 2)];
    pie = e === 2 && u.correo;
  } else if (n === 1) {
    const k = Math.min(3, Math.floor(p * 5));
    cab = u.bandeja; chip = k >= 3 ? ['ok', u.leidas] : ['info', u.leyendo(Math.max(1, k + 1))];
    filas = [fila('Empaques del Valle', '₡612,400', k >= 1), fila('Transportes Pacífico', '₡185,000', k >= 2), fila('Compañía Eléctrica del Valle', '₡94,320', k >= 3)];
    pie = k >= 3 && u.propuestas;
  } else if (n === 2) {
    const k = Math.min(3, Math.floor(p * 5));
    cab = u.asiento; chip = k >= 3 ? ['ok', u.cuadrado] : ['info', u.posteando];
    filas = [fila('Freight · Logistics', '310,000 Dr', k >= 1), fila('VAT credit', '40,300 Dr', k >= 2), fila('Suppliers', '350,300 Cr', k >= 3)];
    pie = k >= 3 && u.contabilizado;
  } else {
    const lleno = Math.min(1, p / 0.6) * 100;
    cab = u.oc; chip = lleno > 80 ? ['mal', u.sobre] : ['ok', u.dentro];
    filas = [fila(u.presupuesto, '₡2,000,000', true), fila(u.gastado, '₡1,825,000', true)];
    barra = (
      <div style={{ height: 10, background: '#E2E5EF', borderRadius: 999, marginTop: 14, overflow: 'hidden', display: 'flex' }} aria-hidden="true">
        <div style={{ width: `${Math.min(lleno, 80)}%`, background: '#4652B8' }} /><div style={{ width: `${Math.max(lleno - 80, 0)}%`, background: '#D0493B' }} />
      </div>);
    pie = lleno > 80 && u.permiso;
  }
  const tile = t.tiles[n];
  return (
    <div className="mosaico">
      <div className="lienzo">
        <div className="ui">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span className="display" style={{ fontSize: 18, fontWeight: 500, letterSpacing: '-.01em' }}>{cab}</span>
            <span className={`chip ${chip[0]}`}>{chip[1]}</span>
          </div>
          {filas}{barra}
          <div style={{ marginTop: 14, fontSize: 12.5, color: '#6B7289', minHeight: 20, opacity: pie ? 1 : 0, transition: 'opacity .4s' }}>{pie || ' '}</div>
        </div>
        <button type="button" className="pausa" onClick={alternar} aria-label={pausa ? u.reproducir : u.pausar}>{pausa ? '▶' : '❚❚'}</button>
      </div>
      <h3 className="display" style={{ fontWeight: 500, fontSize: 21, margin: '22px 0 0', color: '#1A1D4E', letterSpacing: '-.01em' }}>{tile.titulo}</h3>
      <p className="suave" style={{ margin: '8px 0 0', maxWidth: 520 }}>{tile.texto}</p>
    </div>
  );
}

function Globo({ etiqueta }) {
  return (
    <svg className="globo" viewBox="0 0 400 400" style={{ width: '100%', maxWidth: 420, height: 'auto' }} role="img" aria-label={etiqueta}>
      <circle cx="200" cy="200" r="190" fill="none" stroke="#CDD2E1" />
      <circle cx="200" cy="200" r="150" fill="none" stroke="#E2E5EF" />
      {[0, -1.5, -3, -4.5, -6, -7.5].map(d => <ellipse key={d} cx="200" cy="200" rx="150" ry="150" fill="none" stroke="#AEB4DD" style={{ animationDelay: `${d}s` }} />)}
      <line x1="50" y1="200" x2="350" y2="200" stroke="#AEB4DD" />
      <line x1="70" y1="125" x2="330" y2="125" stroke="#E2E5EF" />
      <line x1="70" y1="275" x2="330" y2="275" stroke="#E2E5EF" />
      <g className="orbita"><circle cx="200" cy="10" r="5" fill="#4652B8" /><circle cx="390" cy="200" r="4" fill="#0E6BA2" /><circle cx="66" cy="334" r="4" fill="#6FBC55" /></g>
      <circle cx="168" cy="214" r="6" fill="#D0493B" /><circle cx="168" cy="214" r="14" fill="none" stroke="#D0493B" />
      <circle cx="146" cy="196" r="6" fill="#4652B8" /><circle cx="146" cy="196" r="14" fill="none" stroke="#4652B8" />
      <text x="182" y="236" fontSize="12" fontFamily="IBM Plex Mono, monospace" fill="#3D4358">CR</text>
      <text x="112" y="186" fontSize="12" fontFamily="IBM Plex Mono, monospace" fill="#3D4358">SV</text>
    </svg>
  );
}

const Puntos = ({ lista }) => (
  <ul className="puntos">{lista.map(([b, s]) => <li key={b}><div><b>{b}</b><span>{s}</span></div></li>)}</ul>
);

export default function Inicio() {
  const T = useT();
  const t = T.inicio;
  const location = useLocation();
  const { desde } = usePlanes();
  useMeta(T.meta.inicio);

  // Los enlaces del menú a secciones (/#suite, /#countries) llegan con hash
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 60);
  }, [location.hash]);

  return (
    <Marco>
      <Portada />
      <Recorrido />
      <VerloCorrer />

      <section style={{ background: '#F4F5FA' }}>
        <div className="caja" style={{ paddingTop: 140, paddingBottom: 140 }}>
          <h2 className="display h2" style={{ color: '#1A1D4E', maxWidth: 900 }}>{t.cierreSemana}</h2>
          <div className="mosaicos">{[0, 1, 2, 3].map(n => <Mosaico key={n} n={n} desfase={n * 1.5} />)}</div>
        </div>
      </section>

      <section id="ai" className="noche">
        <div className="caja" style={{ paddingTop: 150, paddingBottom: 150 }}>
          <div style={{ maxWidth: 860 }}>
            <div className="ojo">{t.ia.ojo}</div>
            <h2 className="display" style={{ fontSize: 'clamp(38px,5vw,68px)', lineHeight: 1.02, margin: '16px 0 0' }}>{t.ia.titulo}</h2>
            <p className="suave" style={{ fontSize: 19, margin: '24px 0 0', maxWidth: 640 }}>{t.ia.texto}</p>
          </div>
          <div className="dos" style={{ marginTop: 64 }}>
            <div className="texto"><Puntos lista={t.ia.puntos} /></div>
            <div className="medio">
              <Ventana clip="ai" etiqueta={t.ia.titulo} />
              <div className="mono" style={{ fontSize: 12, color: '#AEB4DD', marginTop: 14 }}>{t.ia.nota}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="reports">
        <div className="caja" style={{ paddingTop: 150, paddingBottom: 120 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div style={{ maxWidth: 760 }}>
              <div className="ojo">{t.rep.ojo}</div>
              <h2 className="display h2" style={{ color: '#1A1D4E', margin: '14px 0 0' }}>{t.rep.titulo}</h2>
            </div>
            <p className="suave" style={{ fontSize: 18, margin: 0, maxWidth: 440 }}>{t.rep.bajada}</p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, marginTop: 64 }}>
            {[['tax', t.rep.impTitulo, t.rep.imp], ['reports', t.rep.repTitulo, t.rep.rep]].map(([clip, titulo, lista]) => (
              <div key={clip} style={{ flex: '1 1 520px', minWidth: 0 }}>
                <Ventana clip={clip} etiqueta={titulo} />
                <h3 className="display" style={{ fontWeight: 500, fontSize: 24, color: '#1A1D4E', margin: '26px 0 14px', letterSpacing: '-.01em' }}>{titulo}</h3>
                <Puntos lista={lista} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="countries" style={{ background: '#FAFBFE', borderTop: '1px solid #E2E5EF', borderBottom: '1px solid #E2E5EF' }}>
        <div className="caja dos" style={{ paddingTop: 140, paddingBottom: 140, gap: 72, alignItems: 'center' }}>
          <div style={{ flex: '1 1 420px', display: 'flex', justifyContent: 'center' }}><Globo etiqueta={t.paises.globo} /></div>
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>
            <div className="ojo">{t.paises.ojo}</div>
            <h2 className="display" style={{ fontSize: 'clamp(34px,4vw,54px)', lineHeight: 1.06, color: '#1A1D4E', margin: '14px 0 0' }}>{t.paises.titulo}</h2>
            <p className="suave" style={{ fontSize: 18, margin: '18px 0 0', maxWidth: 560 }}>{t.paises.texto}</p>
            <div className="lineas" style={{ marginTop: 36, borderTop: '1px solid #CDD2E1' }}>
              {t.paises.lista.map(([pais, texto, listo]) => (
                <div key={pais} style={{ display: 'flex', gap: 16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 260px' }}><div style={{ fontWeight: 600, color: '#1A1D4E' }}>{pais}</div><p className="suave" style={{ margin: '6px 0 0' }}>{texto}</p></div>
                  {listo ? <span className="chip ok">{t.paises.disponible}</span> : <Link to="/demo" style={{ fontSize: 14, fontWeight: 600 }}>{t.paises.hablemos} ›</Link>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="suite">
        <div className="caja" style={{ paddingTop: 130, paddingBottom: 130 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <h2 className="display" style={{ fontSize: 'clamp(34px,4vw,56px)', lineHeight: 1.05, color: '#1A1D4E', maxWidth: 760 }}>{t.suite.titulo}</h2>
            <p className="suave" style={{ fontSize: 17, margin: 0, maxWidth: 380 }}>{t.suite.texto}</p>
          </div>
          <div className="tres" style={{ marginTop: 64 }}>
            <a href="https://payroll360lite.com/"><div className="display" style={{ fontSize: 26, fontWeight: 500 }}>Payroll360</div><p className="suave" style={{ margin: '10px 0 0' }}>{t.suite.payroll}</p><div style={{ color: '#4652B8', fontWeight: 500, marginTop: 16 }}>payroll360lite.com ›</div></a>
            <a href="https://kmessage.net/"><div className="display" style={{ fontSize: 26, fontWeight: 500 }}>KMessage</div><p className="suave" style={{ margin: '10px 0 0' }}>{t.suite.kmessage}</p><div style={{ color: '#4652B8', fontWeight: 500, marginTop: 16 }}>kmessage.net ›</div></a>
            <div><div className="display" style={{ fontSize: 26, fontWeight: 500 }}>Unity360</div><p className="suave" style={{ margin: '10px 0 0' }}>{t.suite.unity}</p></div>
          </div>
        </div>
      </section>

      <section style={{ background: '#F4F5FA' }}>
        <div className="caja" style={{ paddingTop: 110, paddingBottom: 110, display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 className="display" style={{ fontSize: 'clamp(32px,3.6vw,50px)', lineHeight: 1.06, color: '#1A1D4E' }}>{t.precios.titulo}</h2>
            <p className="suave" style={{ fontSize: 18, margin: '14px 0 0' }}>{t.precios.texto(desde ?? 69)}</p>
          </div>
          <Link to="/pricing" className="pildora primaria" style={{ minHeight: 52 }}>{t.precios.boton}</Link>
        </div>
      </section>

      <section className="noche" style={{ textAlign: 'center' }}>
        <div className="caja" style={{ paddingTop: 160, paddingBottom: 160 }}>
          <h2 className="display" style={{ fontSize: 'clamp(40px,5.6vw,80px)', lineHeight: 1, maxWidth: 900, margin: '0 auto' }}>{t.cierre.titulo}</h2>
          <p style={{ fontSize: 19, color: '#E4E7F7', margin: '24px auto 0', maxWidth: 600 }}>{t.cierre.texto}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 40 }}>
            <Link to="/demo" className="pildora blanca" style={{ minHeight: 52 }}>{T.nav.demo}</Link>
            <a href="mailto:info@kuantabridge.com" className="pildora borde" style={{ minHeight: 52 }}>{t.cierre.ventas}</a>
          </div>
        </div>
      </section>
    </Marco>
  );
}
